import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Navbar from './Navbar';
import WorkWallH from './WorkWallH';
import Admin from './Admin';
import WorkDetail3 from './WorkDetail3';
import { SiteGate } from './SiteGate';
import Home from './Home';
import Info from './Info';
import Message from './Message';
import CategoryBackground from './CategoryBackground';

const PAGE_TRANSITION_MS = 320;
const MOBILE_WIDTH = 900; // en dessous : mise en page et curseur tactiles

const initialModule = window.location.hash === '#admin' ? 'admin' : 'home';

export default function App() {
  const [activeModule, setActiveModule] = useState(initialModule);
  const [displayModule, setDisplayModule] = useState(initialModule);
  const [isLeaving, setIsLeaving] = useState(false);
  const [selectedWork, setSelectedWork] = useState(null);
  const [siteUnlocked, setSiteUnlocked] = useState(
    () => localStorage.getItem('oblik_site_access') === '1'
  );
  // Intro d'accueil : les travaux défilent en plein écran, seul le logo bleu
  // est visible. Au premier mouvement / clic, le site normal apparaît en fondu.
  const [intro, setIntro] = useState(initialModule === 'home');
  const [introLeaving, setIntroLeaving] = useState(false);
  // Détail d'un travail : le logo/navbar s'efface en fondu (pas de coupure brutale)
  const [detailMode, setDetailMode] = useState(false);
  const detailModeRef = useRef(false);

  useEffect(() => {
    const onResize = () => {
      // La position du pill goo est calculée au mouvement de souris : après un
      // redimensionnement elle est périmée et se décale du vrai logo (on voyait
      // alors les deux). On masque la couche : elle se replacera correctement
      // au prochain passage du curseur près du logo.
      if (logoGooLayerRef.current) logoGooLayerRef.current.style.visibility = 'hidden';
      if (logoGooPillRef.current) logoGooPillRef.current.style.visibility = 'hidden';
      if (cursorRef.current) {
        cursorRef.current.classList.remove('cursor-melted');
        // En passant sur un format tactile (rotation, fenêtre réduite), la
        // goutte doit se remettre en veille : elle ne réapparaîtra qu'au
        // prochain contact du doigt.
        if (window.innerWidth < MOBILE_WIDTH) {
          cursorRef.current.classList.remove('cursor-on');
        }
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Sortie de l'intro au premier signe d'activité de l'utilisateur
  useEffect(() => {
    if (!intro || !siteUnlocked) return undefined;
    let done = false;
    const events = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'];
    const dismiss = () => {
      if (done) return;
      done = true;
      setIntroLeaving(true);
      setTimeout(() => setIntro(false), 900); // durée du fondu de sortie
    };
    // court délai de grâce : le clic de déverrouillage ne coupe pas l'intro
    const arm = setTimeout(() => {
      events.forEach((ev) => window.addEventListener(ev, dismiss, { passive: true }));
    }, 600);
    return () => {
      clearTimeout(arm);
      events.forEach((ev) => window.removeEventListener(ev, dismiss));
    };
  }, [intro, siteUnlocked]);

  // Pendant l'intro : la nav (hors logo) et le contenu sont masqués
  useEffect(() => {
    document.body.classList.toggle('intro-active', intro && !introLeaving);
    return () => document.body.classList.remove('intro-active');
  }, [intro, introLeaving]);

  // Garde la ref à jour pour moveCursor (créé une seule fois)
  useEffect(() => {
    detailModeRef.current = detailMode;
  }, [detailMode]);

  // Page Work : sur téléphone les travaux défilent sous la nav, qui passe
  // donc en mode différence pour rester lisible par-dessus les visuels.
  useEffect(() => {
    document.body.classList.toggle('work-page', displayModule === 'work');
    return () => document.body.classList.remove('work-page');
  }, [displayModule]);

  // Page admin : c'est un outil de travail, pas une vitrine — on y rend le
  // curseur système (champs, boutons, sélections) et on range la goutte.
  useEffect(() => {
    document.body.classList.toggle('admin-mode', displayModule === 'admin');
    return () => document.body.classList.remove('admin-mode');
  }, [displayModule]);

  const cursorRef = useRef(null);
  const lastPos = useRef({ x: 0, y: 0 });
  const lastCursor = useRef({ x: 0, y: 0 }); // position réelle de la goutte (magnétisme)
  const logoGooLayerRef = useRef(null);
  const logoGooDotRef = useRef(null);
  const logoGooPillRef = useRef(null);

  const handleNavClick = (nextModule) => {
    if (nextModule === activeModule && displayModule !== 'workdetail') return;
    setActiveModule(nextModule);
    setDetailMode(false);
    setIsLeaving(true);
    setTimeout(() => {
      setDisplayModule(nextModule);
      setSelectedWork(null);
      setIsLeaving(false);
    }, PAGE_TRANSITION_MS);
  };

  // Ouverture / fermeture de la page détail d'un travail (avec transition).
  // detailMode déclenche le fondu du logo/navbar en même temps que la page.
  const openWork = (work) => {
    setSelectedWork(work);
    setDetailMode(true);
    setIsLeaving(true);
    setTimeout(() => {
      setDisplayModule('workdetail');
      setIsLeaving(false);
    }, PAGE_TRANSITION_MS);
  };

  const closeWork = () => {
    setDetailMode(false);
    setIsLeaving(true);
    setTimeout(() => {
      setDisplayModule('work');
      setSelectedWork(null);
      setIsLeaving(false);
    }, PAGE_TRANSITION_MS);
  };

  useEffect(() => {
    const moveCursor = (e) => {
      const cursor = cursorRef.current;
      if (!cursor) return;
      // Sur téléphone, la goutte est pilotée par le doigt (plus bas). Les
      // navigateurs mobiles émettent des événements souris de synthèse après
      // chaque toucher : sans ce garde-fou, ils rallumeraient la goutte et
      // elle resterait affichée une fois le doigt relevé.
      if (window.innerWidth < MOBILE_WIDTH) return;

      // Métaballes avec le logo : à l'approche, la goutte fusionne avec
      // le logo et fond dedans (aucun magnétisme).
      const gooLayer = logoGooLayerRef.current;
      const gooDot = logoGooDotRef.current;
      const gooPill = logoGooPillRef.current;
      // Sur la page détail, le logo est masqué : pas d'effet goo dessus
      if (gooLayer && gooDot && gooPill && !detailModeRef.current) {
        let target = null;

        const pill = document.querySelector('.logo .pill');
        if (pill) {
          const r = pill.getBoundingClientRect();
          const Z = 45;
          if (
            e.clientX > r.left - Z && e.clientX < r.right + Z &&
            e.clientY > r.top - Z && e.clientY < r.bottom + Z
          ) {
            target = { r, type: 'pill' };
          }
        }

        if (target) {
          const { r } = target;
          gooLayer.style.visibility = 'visible';
          // la forme goo remplace la goutte (classe et non style direct :
          // les règles de masquage des autres effets restent prioritaires)
          cursor.classList.add('cursor-melted');

          gooPill.style.visibility = 'visible';
          gooPill.style.transform = `translate(${r.left}px, ${r.top}px)`;
          gooPill.setAttribute('width', r.width);
          gooPill.setAttribute('height', r.height);

          // Absorption progressive : plus la goutte s'enfonce vers le coeur
          // de la cible, plus elle rétrécit — on voit la fonte se faire,
          // en continu, pendant la fusion goo.
          const tcx = r.left + r.width / 2;
          const tcy = r.top + r.height / 2;
          const distC = Math.hypot(e.clientX - tcx, e.clientY - tcy);
          const startR = Math.hypot(r.width, r.height) / 2 + 12; // début de la fonte
          const endR = Math.min(r.width, r.height) / 4;          // fonte totale
          let melt = (distC - endR) / (startR - endR);
          melt = Math.max(0.001, Math.min(1, melt));
          gooDot.style.transition = '';

          const gdx = e.clientX - lastCursor.current.x;
          const gdy = e.clientY - lastCursor.current.y;
          const gSpeed = Math.sqrt(gdx * gdx + gdy * gdy);
          const gAngle = Math.atan2(gdy, gdx) * (180 / Math.PI);
          const gStretch = Math.min(1 + gSpeed / 20, 2.5);
          const sx = gStretch * melt;
          const sy = melt;
          // +15px : même décalage que la goutte normale, pour éviter tout saut
          gooDot.style.transform = `translate(${e.clientX + 15}px, ${e.clientY + 15}px) rotate(${gAngle}deg) scale(${sx}, ${sy})`;
          lastCursor.current = { x: e.clientX, y: e.clientY };
          lastPos.current = { x: e.clientX, y: e.clientY };
          return;
        }
      }
      if (gooLayer) gooLayer.style.visibility = 'hidden';
      cursor.classList.remove('cursor-melted');
      cursor.classList.add('cursor-on'); // révélée au premier mouvement

      // Magnétisme : si un élément "magnet-active" est présent et que le
      // pointeur entre dans sa zone invisible, le curseur est aspiré au centre.
      const magnetEl = document.querySelector('.magnet-active');
      const MAGNET_MARGIN = 50;
      if (magnetEl) {
        const r = magnetEl.getBoundingClientRect();
        const inZone =
          e.clientX > r.left - MAGNET_MARGIN && e.clientX < r.right + MAGNET_MARGIN &&
          e.clientY > r.top - MAGNET_MARGIN && e.clientY < r.bottom + MAGNET_MARGIN;
        if (inZone) {
          const cx = r.left + r.width / 2;
          const cy = r.top + r.height / 2;
          // La goutte garde sa déformation pendant l'aspiration :
          // on l'étire selon le trajet qu'elle effectue vers le centre du bouton
          const mdx = cx - lastCursor.current.x;
          const mdy = cy - lastCursor.current.y;
          const mSpeed = Math.sqrt(mdx * mdx + mdy * mdy);
          const mAngle = Math.atan2(mdy, mdx) * (180 / Math.PI);
          const mStretch = Math.min(1 + mSpeed / 20, 2.5);
          cursor.style.transition = 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)';
          cursor.style.transform = `translate(${cx - 15}px, ${cy - 15}px) rotate(${mAngle}deg) scale(${mStretch}, 1)`;
          magnetEl.classList.add('magnetized');
          lastCursor.current = { x: cx, y: cy };
          lastPos.current = { x: e.clientX, y: e.clientY };
          return;
        }
        magnetEl.classList.remove('magnetized');
      } else {
        const lingering = document.querySelector('.magnetized');
        if (lingering) lingering.classList.remove('magnetized');
      }
      cursor.style.transition = '';

      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      const speed = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      const stretch = Math.min(1 + speed / 20, 2.5);

      cursor.style.transform = `translate(${e.clientX - 15}px, ${e.clientY - 15}px) rotate(${angle}deg) scale(${stretch}, 1)`;
      lastCursor.current = { x: e.clientX, y: e.clientY };

      const sel = document.getSelection();
      if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
        const rects = Array.from(sel.getRangeAt(0).getClientRects());
        const over = rects.some(r =>
          e.clientX >= r.left && e.clientX <= r.right &&
          e.clientY >= r.top && e.clientY <= r.bottom
        );
        cursor.style.borderColor = over ? '#fd7a00' : '#F4EFD9';
      } else {
        cursor.style.borderColor = '#F4EFD9';
      }

      lastPos.current = { x: e.clientX, y: e.clientY };
    };

    document.addEventListener('mousemove', moveCursor);
    // Le curseur natif est masqué par la feuille de style (`* { cursor: none }`)
    // et non ici : un style direct empêcherait la page admin de le rétablir.
    return () => document.removeEventListener('mousemove', moveCursor);
  }, []);

  // Version tactile (téléphone) : la goutte apparaît au contact du doigt,
  // le suit avec la même déformation que sur ordinateur, puis s'efface au
  // relâcher. Sans les effets de fusion : ils reposent sur l'approche du
  // curseur, qui n'existe pas au toucher, et leur filtre coûte trop cher ici.
  useEffect(() => {
    const derniere = { x: 0, y: 0 };
    let active = false;
    let attente = null; // délai avant apparition (voir onStart)
    const DELAI_MAINTIEN = 140; // ms : en dessous, c'est un simple tapotement

    const placer = (t, deformer) => {
      const cursor = cursorRef.current;
      if (!cursor) return;
      const dx = t.clientX - derniere.x;
      const dy = t.clientY - derniere.y;
      const vitesse = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      const etirement = deformer ? Math.min(1 + vitesse / 20, 2.5) : 1;
      cursor.style.transform =
        `translate(${t.clientX - 15}px, ${t.clientY - 15}px) rotate(${angle}deg) scale(${etirement}, 1)`;
      derniere.x = t.clientX;
      derniere.y = t.clientY;
    };

    const onStart = (e) => {
      if (window.innerWidth >= MOBILE_WIDTH) return; // téléphone uniquement
      const t = e.touches && e.touches[0];
      const cursor = cursorRef.current;
      if (!t || !cursor) return;
      derniere.x = t.clientX;
      derniere.y = t.clientY;
      placer(t, false); // pose la goutte sans étirement parasite
      active = true;
      // La goutte n'apparaît que si le doigt reste posé : un simple
      // tapotement (ou un clic sur un lien) ne doit rien afficher.
      clearTimeout(attente);
      attente = setTimeout(() => cursor.classList.add('cursor-on'), DELAI_MAINTIEN);
    };

    const onMove = (e) => {
      if (!active) return;
      const t = e.touches && e.touches[0];
      const cursor = cursorRef.current;
      if (!t || !cursor) return;
      placer(t, true);
      // Dès que le doigt glisse, la goutte se montre sans attendre le délai
      clearTimeout(attente);
      cursor.classList.add('cursor-on');
    };

    const onEnd = () => {
      clearTimeout(attente); // un tapotement n'aura jamais rien affiché
      if (!active) return;
      active = false;
      const cursor = cursorRef.current;
      if (!cursor) return;
      cursor.classList.remove('cursor-on'); // disparition immédiate
    };

    const opts = { passive: true };
    window.addEventListener('touchstart', onStart, opts);
    window.addEventListener('touchmove', onMove, opts);
    window.addEventListener('touchend', onEnd, opts);
    window.addEventListener('touchcancel', onEnd, opts);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  // Sur les pages sans logo (détail d'un travail, admin) : on force le
  // masquage de la couche "logo goo". Sinon, si le curseur a frôlé le logo
  // juste avant la navigation puis s'est immobilisé, le pill bleu resterait
  // figé en haut à gauche (moveCursor ne se rejoue qu'au prochain mouvement).
  useEffect(() => {
    const noLogo = detailMode || displayModule === 'admin';
    if (noLogo && logoGooLayerRef.current) {
      logoGooLayerRef.current.style.visibility = 'hidden';
      if (logoGooPillRef.current) logoGooPillRef.current.style.visibility = 'hidden';
      if (cursorRef.current) cursorRef.current.classList.remove('cursor-melted');
    }
  }, [detailMode, displayModule]);

  // Porte d'entrée : code d'accès (3 tentatives)
  if (!siteUnlocked) {
    return (
      <div className="app-container">
        {/* La goutte : invisible au départ (CSS), révélée au premier mouvement
          de souris ou au premier contact du doigt. L'opacité n'est plus posée
          ici en style React, sinon un nouveau rendu la ferait disparaître en
          plein toucher. */}
      <div ref={cursorRef} className="custom-cursor" />
        <SiteGate onUnlock={() => setSiteUnlocked(true)} />
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* La goutte : invisible au départ (CSS), révélée au premier mouvement
          de souris ou au premier contact du doigt. L'opacité n'est plus posée
          ici en style React, sinon un nouveau rendu la ferait disparaître en
          plein toucher. */}
      <div ref={cursorRef} className="custom-cursor" />

      {/* Couche métaballes du logo : la goutte fusionne avec le logo */}
      <div ref={logoGooLayerRef} className="logo-goo-layer" aria-hidden="true">
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <filter id="logo-goo-filter">
              <feGaussianBlur in="SourceGraphic" stdDeviation="11" result="blur" />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 26 -12"
              />
            </filter>
          </defs>
        </svg>
        <div className="logo-goo-shapes">
          <svg
            ref={logoGooPillRef}
            className="logo-goo-pill"
            viewBox="0 0 59 78"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M10.9603 50.618C4.01302 38.977 -2.08086 24.509 0.679386 10.4544C4.47973 -8.88065 28.9219 1.08024 43.7766 20.322C52.7907 31.9897 63.6317 55.1518 56.4844 70.9932C48.857 87.888 22.4147 70.5398 11.0136 50.7247L10.947 50.618H10.9603Z" fill="#0050ff" />
          </svg>
          <div ref={logoGooDotRef} className="logo-goo-dot" />
        </div>
      </div>

      {/* Intro : travaux en plein écran qui défilent, seul le logo reste visible */}
      {siteUnlocked && intro && (
        <div className={`intro-overlay${introLeaving ? ' intro-overlay--leaving' : ''}`} aria-hidden="true">
          <CategoryBackground cat="tous" />
        </div>
      )}

      {/* La navbar reste montée sur le détail mais s'efface en fondu
          (detailMode), pour une disparition/apparition harmonieuse du logo */}
      {displayModule !== 'admin' && (
        <div className={`Nav-module${detailMode ? ' Nav-module--hidden' : ''}`}>
          <Navbar onNavClick={handleNavClick} activeModule={activeModule} />
        </div>
      )}
      <div className={`content-module${isLeaving ? ' content-module--leaving' : ''}`}>
        {displayModule === 'home' && <Home onNavClick={handleNavClick} />}
        {/* Page Work : version K (mur horizontal), qui gère elle-même
            son affichage mobile (travaux empilés) */}
        {displayModule === 'work' && <WorkWallH onOpenWork={openWork} />}
        {/* Page détail d'un travail : variante C (mix) */}
        {displayModule === 'workdetail' && (
          <WorkDetail3
            key={selectedWork ? selectedWork.label : 'detail3'}
            work={selectedWork}
            onBack={closeWork}
            onOpenWork={openWork}
          />
        )}
        {displayModule === 'message' && <Message />}
        {displayModule === 'info' && <Info />}
        {displayModule === 'admin' && <Admin />}
      </div>
    </div>
  );
}
